import {
    Autoplay,
    createVideoPlayer,
    getAutoplayCapabilities,
    KeyboardScope,
    removeVideoPlayer,
    PlayerEvent,
    PlaybackState,
} from '@cbsinteractive/avia-js';
import { hls } from '@cbsinteractive/avia-js-hls';

import { HLS_VIDEO_PREVIEW_ENABLED } from 'helpers/featureConstants';
import { createLogger } from 'helpers/logger';
import { VideoPreviewEvent } from './videoPreviewConstants';

export async function removeVideoPreview(id) {
    return await removeVideoPlayer(id);
}

export async function supportsUnmutedAutoplay() {
    try {
        const autoplayCapabilities = await getAutoplayCapabilities();

        return autoplayCapabilities.supportsUnmutedAutoplay;
    } catch (e) {
        return false;
    }
}
export class VideoPreview {
    constructor({
        id,
        selector,
        mediaUrl,
        delayStart = 0,
        destroyAfterPlay = true,
        mutedAutoplay = true,
        setQueryParams = true,
    }) {
        this._id = id;
        this._selector = selector;
        this._mediaUrl = mediaUrl;
        this._delayStart = delayStart;
        this._destroyAfterPlay = destroyAfterPlay;
        this._mutedAutoplay = mutedAutoplay;
        this._player = null;
        this._timeout = null;
        this._eventListeners = this.__getEventListeners();
        this._logger = createLogger('VideoPreview');
        this._setQueryParams = setQueryParams;
    }

    get id() {
        return this._id;
    }
    async setPlayer() {
        let shouldAutoplay = false;

        const autoplay = async () => {
            if (shouldAutoplay && this._player) {
                shouldAutoplay = false;
                await this.play();
                // Uncomment for faster testing
                // this.seek(135);
            }
        };

        // Start timer before creating player, so
        // player load time counts towards delay
        this._timeout = setTimeout(async () => {
            // Set to true here, since timeout needs to complete first
            shouldAutoplay = true;
            this._timeout = null;

            try {
                await autoplay();
            } catch (e) {
                this._logger.log(`Failed to autoplay`);
            }
        }, this._delayStart);
        await this.__createPlayer();
        if (this._player) {
            this._player.muted = this._mutedAutoplay;
        }
        await autoplay();
    }

    async __createPlayer() {
        try {
            const autoplayCapabilities = await getAutoplayCapabilities();

            // Don't create player if autoplay is not supported
            if (!autoplayCapabilities.supportsMutedAutoplay) {
                throw new Error('Autoplay not supported');
            }

            const playerOptions = {
                id: this._id,
                container: this._selector,
                keyboard: {
                    scope: KeyboardScope.NONE,
                },
            };

            if (this.useHLS() === true) {
                playerOptions.plugins = [hls()];
            }

            this._logger.log(`Create player for ${this._id}`);

            this._player = await createVideoPlayer(playerOptions);
            const resource = await this.attachResource(this._mediaUrl);

            this._logger.log('Resource attached', resource);

            this.__listenPlayerEvents();
        } catch (error) {
            this._logger.log(error);
            throw new Error('Error creating video preview');
        }
    }

    //If we need to replay the video, we can call this method again or send different mediaUrl to play a different one
    async attachResource(mediaUrl) {
        const preparedMediaUrl =
            this.useHLS() && this._setQueryParams ? this.__setQueryParameters(mediaUrl) : mediaUrl;
        const options = {
            cmcd: {
                enabled: false,
            },
            location: {
                mediaUrl: preparedMediaUrl,
            },
        };

        if (this.useHLS() === true) {
            options.overrides = {
                mimeType: 'application/vnd.apple.mpegurl',
            };
        }

        this._logger.log('Attaching resource', options);

        return await this._player.attachResource(options);
    }

    __setQueryParameters(mediaUrl) {
        const url = new URL(mediaUrl);
        if (CBS.Registry.user?.id) {
            url.searchParams.set('user_id', CBS.Registry.user?.id);
            url.searchParams.set('profile_id', CBS.Registry.user?.profile_id);
        }
        url.searchParams.set('platformType', 'desktop');
        return url.href;
    }

    async removePlayer() {
        try {
            this._logger.log(`Remove player for ${this._id}`);
            clearTimeout(this._timeout);
            this.__removeEventListeners();
            await removeVideoPlayer(this._id);
        } catch (error) {
            this._logger.log(error);
        }
    }

    __getEventListeners() {
        return [
            {
                name: PlayerEvent.AUTOPLAY_BLOCKED,
                handler: this.__onAutoplayBlocked.bind(this),
            },
            {
                name: PlayerEvent.RESOURCE_END,
                handler: this.__onResourceEnd.bind(this),
            },
        ];
    }

    __onAutoplayBlocked() {
        this._logger.log('Autoplay blocked');
    }

    __onResourceEnd() {
        if (this._destroyAfterPlay) {
            this.removePlayer();
        }
    }

    __listenPlayerEvents() {
        this._eventListeners.forEach(({ name, handler }) => {
            this.addEventListener(name, handler);
        });
    }

    __removeEventListeners() {
        this._eventListeners.forEach(({ name, handler }) => {
            this.removeEventListener(name, handler);
        });
        // Reset
        this._eventListeners = [];
    }

    addEventListener(eventName, eventHandler) {
        // Add event for later removal
        this._eventListeners.push({
            name: eventName,
            handler: eventHandler,
        });

        // If player does not exist, listener will be added
        // after player is created
        if (this._player) {
            this._player.on(eventName, eventHandler);
        }
    }

    once(eventName, eventHandler) {
        if (this._player) {
            this._player.once(eventName, eventHandler);
        }
    }

    removeEventListener(eventName, eventHandler) {
        if (this._player) {
            this._player.off(eventName, eventHandler);
        }
    }

    getAudioTracks() {
        // Returns an array of audio tracks, can be empty if no audio multiple audio supported
        return this._player.audioTracks;
    }

    getCurrentAudioTrack() {
        // Returns an object with the current audio track
        return this._player.audioTrack;
    }

    changeAudioTrack(lang) {
        return this._player.selectAudioLanguage(lang);
    }

    getContentDuration() {
        return this._player.contentDuration;
    }

    getPlaybackState() {
        return this._player.playbackState;
    }

    isMuted() {
        return this._player.muted;
    }

    toggleMuted() {
        this._player.toggleMuted();
    }

    mute(bool) {
        this._player.muted = bool;
    }

    isPaused() {
        return this._player.playbackState === PlaybackState.PAUSED;
    }

    isPlaying() {
        return this._player.playbackState === PlaybackState.PLAYING;
    }

    suspendPlayback() {
        const isWaitingOrIdleState =
            this.getPlaybackState() === PlaybackState.WAITING ||
            this.getPlaybackState() === PlaybackState.IDLE;

        try {
            if (isWaitingOrIdleState) {
                const stateChangeHandler = () => {
                    const currentState = this.getPlaybackState();
                    if (currentState === PlaybackState.PLAYING) {
                        // Remove the listener first to prevent multiple calls
                        this._player.off(
                            VideoPreviewEvent.PLAYBACK_STATE_CHANGE,
                            stateChangeHandler,
                        );
                        this._player.suspendPlayback();
                    }
                };
                this._player.on(VideoPreviewEvent.PLAYBACK_STATE_CHANGE, stateChangeHandler);
            } else {
                this._player.suspendPlayback();
            }
        } catch (error) {
            this._logger.log('Spotlight video suspend error', error);
        }
    }

    resumePlayback() {
        this._player.resumePlayback();
    }

    playerExists() {
        return this._player !== null;
    }

    async pause() {
        this._logger.log('pause');
        return await this._player.pause();
    }

    async play() {
        this._logger.log('play');
        return await this._player.play();
    }

    async replay() {
        return await this.attachResource(this._mediaUrl);
    }

    async seek(position) {
        return await this._player.seek(position);
    }

    useHLS() {
        return CBS.Registry.Features[HLS_VIDEO_PREVIEW_ENABLED];
    }

    currentTime() {
        return this._player.contentTime;
    }
}

export default VideoPreview;
